/**
 * fetch() with an AbortController-based timeout.
 *
 * Almost every outbound HTTP call in this codebase used `fetch()` with no
 * timeout. When a third-party upstream wedges, Node's default socket
 * timeouts can be 5+ minutes — long enough that getServerSideProps
 * handlers stall and the whole route returns a 504 instead of the page.
 *
 * Use this helper for any call that hits a third-party (or even
 * 1Anime-internal) HTTP endpoint where you'd rather fail-fast on a hang
 * than block the request handler indefinitely.
 *
 * Caller can override the default with `timeoutMs`; pass `signal` to
 * compose with another AbortController if needed.
 */

const DEFAULT_TIMEOUT_MS = 12_000;

export interface FetchWithTimeoutInit extends RequestInit {
  timeoutMs?: number;
}

export async function fetchWithTimeout(
  url: string | URL,
  init: FetchWithTimeoutInit = {}
): Promise<Response> {
  const {
    timeoutMs = DEFAULT_TIMEOUT_MS,
    signal: callerSignal,
    ...rest
  } = init;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  // If the caller passed their own signal, abort our controller when they
  // abort. Use addEventListener so we don't replace any other listeners.
  if (callerSignal) {
    if (callerSignal.aborted) {
      controller.abort();
    } else {
      callerSignal.addEventListener('abort', () => controller.abort(), {
        once: true,
      });
    }
  }

  try {
    return await fetch(url, { ...rest, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}
